// trace-viewer/src/behaviors/keyframes.ts
import { TraceBehavior } from "./tracebehavior";
import { Properties } from "./properties";
import type { KeyframesData, KeyframeEntry } from "../scenedata/behaviors/keyframesdata";
import type { TimeTransition, StateDefinition } from "../scenedata/behaviors/propertiesdata";
import { Logger } from "../utils/Logger";
import type { SceneObject } from "../core/SceneObject";

/**
 * Keyframes behavior — named subtree configurations, applied via Properties states.
 *
 * Each keyframe entry is pushed as an external state contribution onto the
 * target descendant's `Properties` resolver. The resolver owns interpolation,
 * cascade, active toggling, and `enterFrom` handling. Keyframes itself does
 * no transition math — it's just an orchestrator that diffs old→new keyframe
 * paths and translates that into push/replace/clear calls.
 *
 * BASE-INHERITANCE SEMANTICS: a keyframe IS its diff from the authored base.
 * Whatever a keyframe doesn't override reverts to authored/base — animated
 * from the current pose, never snapped (the resolver captures display values
 * before recomputing). Switching KF1{pos} → KF2{} animates the object back
 * to its authored pose. This replaced the earlier "carry-forward" semantics
 * (unmentioned facets held the prior keyframe's value), which read as
 * "moving an object on one keyframe affects all the others" in the editor
 * and required fragile per-path facet tracking.
 *
 * Targets must have a `Properties` behavior attached. Entries on targets
 * without one are skipped with a warning.
 */
export class Keyframes extends TraceBehavior<KeyframesData> {
    static readonly behaviorType = "Keyframes" as const;

    private _activeKeyframe: string | null = null;

    /** Paths we currently have contributions on (so clearAll on detach reaches them) */
    private _contributedPaths = new Set<string>();

    /** Stable contributor id derived from the host transform's uniqueId */
    private get _contributorId(): number {
        return this.sceneObject.transform.uniqueId;
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            if (this._resolved.initialKeyframe) {
                void this.goTo(this._resolved.initialKeyframe, false);
            }
        } else {
            // Leaving play mode returns to Base. Keyframe selection is runtime
            // state, not authored data — letting the play-time keyframe linger
            // after stop would silently arm editor capture into a keyframe the
            // user never selected (the card strip auto-sets initialKeyframe on
            // the first add, so every container would hit this).
            void this.goTo(null, false);
        }
    }

    protected onDetach(): void {
        this._clearAllContributions(true);
        this._activeKeyframe = null;
    }

    // ── Public API ──

    /** Get the name of the currently active keyframe, or null if at authored base. */
    getActiveKeyframe(): string | null {
        return this._activeKeyframe;
    }

    /**
     * Re-apply the current keyframe (instant). Used after keyframe data is
     * modified externally (e.g. editor capture). `force=true` bypasses the
     * `oldName === name` early-return in _goToInternal; the underlying
     * Map-keyed `pushContribution` then overwrites the existing entry.
     */
    reapply(): void {
        const name = this._activeKeyframe;
        if (!name) return;
        const keyframes = this._resolved.keyframes;
        if (!keyframes?.[name]) return;
        void this._goToInternal(name, false, true);
    }

    /** Get all keyframe names. */
    getKeyframeNames(): string[] {
        return Object.keys(this._resolved.keyframes ?? {});
    }

    /** Advance to the next keyframe (wraps around). */
    async next(animate = true): Promise<void> {
        const names = this.getKeyframeNames();
        if (names.length === 0) return;
        const idx = this._activeKeyframe ? names.indexOf(this._activeKeyframe) : -1;
        const next = (idx + 1) % names.length;
        await this.goTo(names[next], animate);
    }

    /** Go to the previous keyframe (wraps around). */
    async prev(animate = true): Promise<void> {
        const names = this.getKeyframeNames();
        if (names.length === 0) return;
        const idx = this._activeKeyframe ? names.indexOf(this._activeKeyframe) : -1;
        const prev = idx <= 0 ? names.length - 1 : idx - 1;
        await this.goTo(names[prev], animate);
    }

    /**
     * Navigate to a named keyframe, or clear all keyframe state.
     *
     * @param name — keyframe name, or `null` to revert every affected
     * descendant to its authored pose by clearing this behavior's contributions.
     * @param animate — whether to animate the transition (default true)
     */
    async goTo(name: string | null, animate = true): Promise<void> {
        return this._goToInternal(name, animate, false);
    }

    // ── Internals ──

    private async _goToInternal(name: string | null, animate: boolean, force: boolean): Promise<void> {
        const keyframes = this._resolved.keyframes;
        if (!keyframes) return;

        if (name !== null && !keyframes[name]) return;

        const oldName = this._activeKeyframe;
        if (!force && oldName === name) return;

        this._activeKeyframe = name;

        const defaultTransition: TimeTransition = {
            duration: this._resolved.transitionDuration ?? 0.5,
            easing: this._resolved.transitionEasing ?? 'easeInOut',
        };

        // Navigating to null: clear everything in one pass and bail.
        if (!name) {
            this._clearAllContributions(!animate, defaultTransition);
            return;
        }

        const newDef = keyframes[name];
        const newEntries = newDef.entries ?? {};
        const keyframeTransition = newDef.transition ?? defaultTransition;
        const oldEntries = oldName ? (keyframes[oldName]?.entries ?? {}) : {};

        const nextContributed = new Set<string>();
        for (const [path, newEntry] of Object.entries(newEntries)) {
            const obj = this._resolveTarget(path);
            if (!obj) continue;
            const props = obj.getBehavior(Properties);
            if (!props) {
                Logger.warn(
                    `[Keyframes] Skipping entry for path "${path}" — target has no Properties behavior. ` +
                    `Keyframe targets must declare Properties to receive state contributions.`,
                );
                continue;
            }

            const def = this._entryToStateDefinition(newEntry, keyframeTransition);
            // enterFrom is a one-shot entry offset, seeded only from the
            // immediately prior keyframe's entry for the same path.
            const oldEnterFrom = oldEntries[path]?.enterFrom;
            if (def.enterFrom === undefined && oldEnterFrom !== undefined) {
                def.enterFrom = oldEnterFrom;
            }

            if (oldName && this._contributedPaths.has(path)) {
                // Path was contributed by the previous keyframe — swap names
                // atomically so the resolver interpolates from the prior pose
                // to the new pose without a snap-to-authored in between.
                props.replaceContribution(
                    this._contributorId,
                    oldName,
                    name,
                    def,
                    !animate,
                    def.transition,
                );
            } else {
                // Fresh push (either oldName is null, or the path wasn't in
                // the old keyframe). pushContribution handles this directly.
                props.pushContribution(this._contributorId, name, def, !animate, def.transition);
            }
            nextContributed.add(path);
        }

        // Base-inheritance: paths the previous keyframe touched but this one
        // doesn't revert to authored/base. The resolver captures current
        // display values before the recompute, so the revert animates from
        // the visible pose — no snap.
        for (const path of this._contributedPaths) {
            if (nextContributed.has(path)) continue;
            const obj = this._resolveTarget(path);
            const props = obj?.getBehavior(Properties);
            if (props) {
                props.clearAllContributions(this._contributorId, !animate, keyframeTransition);
            }
        }

        this._contributedPaths = nextContributed;
    }

    private _entryToStateDefinition(entry: KeyframeEntry, fallbackTransition: TimeTransition): StateDefinition {
        const def: StateDefinition = {
            // Shallow-clone so the contribution never aliases the _resolved
            // entry (enterFrom seeding writes into the def).
            values: { ...(entry.values ?? {}) },
            transition: entry.transition ?? fallbackTransition,
        };
        if (entry.transform) def.transform = entry.transform;
        if (entry.active !== undefined) def.active = entry.active;
        if (entry.enterFrom) def.enterFrom = entry.enterFrom;
        return def;
    }

    private _resolveTarget(path: string): SceneObject | null {
        if (path === './') return this.sceneObject;
        return this.sceneObject.traceScene.resolveSceneObject(path, this.sceneObject) ?? null;
    }

    private _clearAllContributions(instant: boolean, transitionFallback?: TimeTransition): void {
        for (const path of this._contributedPaths) {
            const obj = this._resolveTarget(path);
            const props = obj?.getBehavior(Properties);
            if (props) {
                props.clearAllContributions(this._contributorId, instant, transitionFallback);
            }
        }
        this._contributedPaths.clear();
    }
}
